import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Badge } from '@/components/ui/badge'
import {
  Merge,
  AlertTriangle,
  User,
  Mail,
  Phone,
  CreditCard,
  Hash,
  ArrowRight,
  Database,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import {
  DriverMaster,
  DriverMergePreview,
  DriverMergeOptions
} from '@/types/driverMaster'

interface DriverMergeDialogProps {
  primaryDriver: DriverMaster
  secondaryDriver: DriverMaster
  preview: DriverMergePreview | null
  open: boolean
  onClose: () => void
  onConfirm: (options: DriverMergeOptions) => Promise<void>
  loading: boolean
  previewLoading: boolean
}

export function DriverMergeDialog({
  primaryDriver,
  secondaryDriver,
  preview,
  open,
  onClose,
  onConfirm,
  loading,
  previewLoading
}: DriverMergeDialogProps) {
  const [resolvedOptions, setResolvedOptions] = useState<DriverMergeOptions>({
    primaryName: primaryDriver.primaryName
  })
  const [showAliasDetails, setShowAliasDetails] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Update resolved options when preview changes
  useEffect(() => {
    if (preview) {
      setResolvedOptions({
        primaryName: preview.conflicts.primaryName
          ? primaryDriver.primaryName
          : primaryDriver.primaryName,
        email: preview.conflicts.email
          ? primaryDriver.email
          : primaryDriver.email || secondaryDriver.email,
        phone: preview.conflicts.phone
          ? primaryDriver.phone
          : primaryDriver.phone || secondaryDriver.phone,
        personalCode: preview.conflicts.personalCode
          ? primaryDriver.personalCode
          : primaryDriver.personalCode || secondaryDriver.personalCode,
        taxiCode: preview.conflicts.taxiCode
          ? primaryDriver.taxiCode
          : primaryDriver.taxiCode || secondaryDriver.taxiCode
      })
    }
  }, [preview, primaryDriver, secondaryDriver])

  const handleSubmit = async () => {
    try {
      setError(null)

      if (!resolvedOptions.primaryName?.trim()) {
        setError('Förarens namn är obligatoriskt')
        return
      }

      await onConfirm(resolvedOptions)
    } catch (error) {
      console.error('Error confirming merge:', error)
      setError(error instanceof Error ? error.message : 'Ett okänt fel inträffade')
    }
  }

  const updateOption = (field: keyof DriverMergeOptions, value: string) => {
    setResolvedOptions(prev => ({
      ...prev,
      [field]: value || undefined
    }))
  }

  const renderConflictResolution = (
    field: keyof DriverMergeOptions,
    label: string,
    icon: React.ReactNode,
    primaryValue?: string,
    secondaryValue?: string
  ) => {
    const hasConflict = primaryValue && secondaryValue && primaryValue !== secondaryValue
    const resolvedValue = resolvedOptions[field] || ''

    if (!hasConflict && !primaryValue && !secondaryValue) {
      return null
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          {icon}
          <Label className="font-medium">{label}</Label>
          {hasConflict && <Badge variant="destructive" className="text-xs">Konflikt</Badge>}
        </div>

        {hasConflict ? (
          <RadioGroup
            value={resolvedValue}
            onValueChange={(value) => updateOption(field, value)}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2 p-2 border rounded">
              <RadioGroupItem value={primaryValue} id={`${field}-primary`} />
              <label htmlFor={`${field}-primary`} className="flex-1 cursor-pointer">
                <span className="text-sm text-muted-foreground">Primär: </span>
                <span className="font-medium">{primaryValue}</span>
              </label>
            </div>
            <div className="flex items-center space-x-2 p-2 border rounded">
              <RadioGroupItem value={secondaryValue} id={`${field}-secondary`} />
              <label htmlFor={`${field}-secondary`} className="flex-1 cursor-pointer">
                <span className="text-sm text-muted-foreground">Sekundär: </span>
                <span className="font-medium">{secondaryValue}</span>
              </label>
            </div>
          </RadioGroup>
        ) : (
          <Input
            value={resolvedValue}
            onChange={(e) => updateOption(field, e.target.value)}
            placeholder={`Ange ${label.toLowerCase()}`}
            className="w-full"
          />
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Slå samman förare
          </DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {previewLoading ? (
          <div className="py-8 text-center">
            <SpinnerIcon size="lg" className="text-primary mx-auto" />
            <p className="mt-2 text-sm text-muted-foreground">Genererar förhandsgranskning...</p>
          </div>
        ) : preview ? (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <ArrowRight className="h-4 w-4" />
                Sammanslagning
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                <span className="font-medium">{secondaryDriver.primaryName}</span> kommer att slås samman med{' '}
                <span className="font-medium">{primaryDriver.primaryName}</span>
              </p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Database className="h-4 w-4" />
                  <span>{preview.earningsToTransfer} inkomstposter kommer att överföras</span>
                </div>
                {preview.duplicateAliases.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {preview.duplicateAliases.length} dubbletter hittades
                  </Badge>
                )}
              </div>
            </div>

            {/* Conflict Resolution */}
            <div className="space-y-4">
              <h3 className="font-medium">Lös konflikter</h3>

              {renderConflictResolution(
                'primaryName',
                'Förarens namn',
                <User className="h-4 w-4" />,
                preview.conflicts.primaryName?.primary,
                preview.conflicts.primaryName?.secondary
              )}

              {renderConflictResolution(
                'email',
                'E-post',
                <Mail className="h-4 w-4" />,
                preview.conflicts.email?.primary,
                preview.conflicts.email?.secondary
              )}

              {renderConflictResolution(
                'phone',
                'Telefon',
                <Phone className="h-4 w-4" />,
                preview.conflicts.phone?.primary,
                preview.conflicts.phone?.secondary
              )}

              {renderConflictResolution(
                'personalCode',
                'Personnummer',
                <Hash className="h-4 w-4" />,
                preview.conflicts.personalCode?.primary,
                preview.conflicts.personalCode?.secondary
              )}

              {renderConflictResolution(
                'taxiCode',
                'Taxikod',
                <CreditCard className="h-4 w-4" />,
                preview.conflicts.taxiCode?.primary,
                preview.conflicts.taxiCode?.secondary
              )}
            </div>

            {/* Alias Details */}
            <div className="space-y-3">
              <button
                onClick={() => setShowAliasDetails(!showAliasDetails)}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary"
              >
                {showAliasDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Alias-detaljer ({preview.mergedAliases.length} totalt)
              </button>

              {showAliasDetails && (
                <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                  <div>
                    <h4 className="text-sm font-medium mb-2">Sammanslagna alias</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {preview.mergedAliases.map((alias, index) => (
                        <div key={index} className="text-xs p-2 bg-background border rounded">
                          <div className="font-medium">{alias.platform}</div>
                          {alias.name && <div className="text-muted-foreground">{alias.name}</div>}
                          {alias.email && <div className="text-muted-foreground">{alias.email}</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {preview.duplicateAliases.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-amber-600">
                        Dubletter (kommer att ignoreras)
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {preview.duplicateAliases.map((alias, index) => (
                          <div key={index} className="text-xs p-2 bg-amber-50 border border-amber-200 rounded">
                            <div className="font-medium">{alias.platform}</div>
                            {alias.name && <div className="text-muted-foreground">{alias.name}</div>}
                            {alias.email && <div className="text-muted-foreground">{alias.email}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Warning */}
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Varning:</strong> Denna åtgärd kan inte ångras. Föraren "{secondaryDriver.primaryName}"
                kommer att markeras som inaktiv och all data kommer att överföras till "{primaryDriver.primaryName}".
              </AlertDescription>
            </Alert>
          </div>
        ) : null}

        <Separator />

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Avbryt
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || previewLoading || !preview}
            className="min-w-[120px]"
          >
            {loading ? (
              <SpinnerIcon size="sm" className="text-white" />
            ) : (
              <>
                <Merge className="h-4 w-4 mr-2" />
                Slå samman
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}